import { Chevron } from './Charts.jsx'
import HoopsTabs from './HoopsTabs.jsx'
import { useCarriedSeason, useSettled } from './historyStore.jsx'

// What every season view draws above its content, and the frame that holds it. P6-329c (GN-2c).
//
// ═══════════════════════════════════════════════════════════════════════════
// THE SAME HEAD ON EVERY VIEW, SO THE SEASON CONTROL IS ONE SHAPE EVERYWHERE
// ═══════════════════════════════════════════════════════════════════════════
//
// The design's rule: *"Every view shows the same two segments. A season with no file for that view is drawn dimmed and
// can't be tapped, so the control stays the same shape everywhere."* The segments are the seasons the bot has published
// (`historyStore.js::useSeasonChoice`, from `/api/hoops/history/seasons`) and nothing typed here: a third season joins
// every switcher the day its manifest exists. A season the view KNOWS has nothing for it (a rookie's page has no 2024-25
// file, which the comparison's own request already found out) is `disabled`, drawn dimmed. A season the view has not
// asked about is enabled, and choosing it is how a member finds out.
//
// ═══════════════════════════════════════════════════════════════════════════
// THE LINE UNDER IT IS THE FILE'S OWN DATE, AND ONLY A CURRENT SEASON'S GOES STALE
// ═══════════════════════════════════════════════════════════════════════════
//
// `seasonModel.js::headModel` writes it: a FINISHED season reads `2025-26 · REGULAR SEASON · FINAL` and
// `NBA · AS OF SAT 3 OCT, 21:03 EDT`, with no age and nothing that can go stale (RECONCILIATION-seasons.md, rule 1).
// `head.stale` is true only for a season marked current whose file has stopped refreshing, and then the amber dot, the
// one sentence about when, and (through `data-stale`, which the view puts on its root) the dashed borders and the
// faded washes are all drawn. No error copy, and nothing live: no live dot and no ticking clock anywhere on these pages.

/**
 * The frame every season view is drawn in: the tab bar, and (once the seasons and the teams have answered) its content.
 * Before that the page is the header and the tab bar and nothing else, which is the design's own rule: no skeletons.
 */
export function ViewFrame({ tab, children }) {
  const settled = useSettled()
  const season = useCarriedSeason()
  return (
    <>
      <HoopsTabs current={tab} season={season} />
      {settled ? children : null}
    </>
  )
}

/**
 * The season switcher, the kicker, the title, the age line and (stale only) the sentence.
 *
 * - `choice`       `{ list, selected, choose }` from `useSeasonChoice`
 * - `unavailable`  seasons this view knows have nothing for it (a Set), drawn dimmed and not tappable
 * - `head`         `seasonModel.js::headModel`'s answer
 * - `title`        the heading, or `null` for a view whose hero is its own heading
 * - `back`         `{ label, onBack }` for a detail page, or `null`
 */
export default function SeasonHead({ choice, unavailable, head, title, back }) {
  return (
    <header className="d2d-hoops__head" data-testid="hoops-season-head">
      <div className="d2d-hoops__headrow">
        {back ? (
          <button
            type="button"
            className="d2d-hoops__crumb"
            aria-label={`Back to ${back.label}`}
            data-testid="hoops-season-back"
            onClick={back.onBack}
          >
            <Chevron direction="left" />
            <span className="d2d-hoops__crumblabel">{back.label}</span>
          </button>
        ) : null}
        <div className="d2d-hoops__seasons" role="group" aria-label="Season" data-testid="hoops-seasons">
          {choice.list.map((entry) => {
            const here = entry.season === choice.selected.season
            return (
              <button
                type="button"
                key={entry.season}
                className="d2d-hoops__season"
                aria-pressed={here ? 'true' : 'false'}
                disabled={!here && unavailable.has(entry.season)}
                data-testid={`hoops-season-${entry.season}`}
                onClick={() => choice.choose(entry.season)}
              >
                {entry.season}
              </button>
            )
          })}
        </div>
      </div>

      <div className="d2d-hoops__kicker d2d-micro" data-testid="hoops-season-kicker">
        {head.kicker}
      </div>
      {title ? (
        <h1 className="d2d-hoops__title" data-testid="hoops-season-title">
          {title}
        </h1>
      ) : null}
      {head.age === null ? null : (
        <div className="d2d-hoops__age">
          {head.stale ? (
            <span className="d2d-hoops__dot d2d-hoops__dot--stale" aria-hidden="true" data-testid="hoops-season-dot" />
          ) : null}
          <span className="d2d-hoops__agetext d2d-micro" data-testid="hoops-season-age">
            {head.age}
          </span>
        </div>
      )}
      {head.stale ? (
        <p className="d2d-hoops__stalenote" data-testid="hoops-season-stale">
          {head.staleNote}
        </p>
      ) : null}
    </header>
  )
}
